import { ISelectOption } from '@common/components/forms';
import { DateTimeUtils } from '@common/utils';
import { filter, includes } from 'lodash';
import { DateTime } from 'luxon';
import {
  AppointmentListingModel,
  AppointmentStatusOptions,
  IModel,
  ModelRecord,
} from '..';
import { AppointmentStatus, ReservationAssignmentStatuses } from '../Enums';

export interface IAppointmentRequest extends IModel {
  reservationId: number | undefined;
  hospitalId: number;
  clientId: number;
  petId: number;
  appointmentTypeId: number;
  appointmentTypeName: string; //extra field UI
  veterinarianId: number | undefined;
  hospitalScheduletemplateId: number | undefined;
  startDate: string;
  endDate: string;
  reasonForVisit: string;
  notes: string;
  appointmentStatus: AppointmentStatus;
  statusId: number;
  length: string;
  duration: number; //extra field UI
  startTime: string; //extra field UI
  endTime: string; //extra field UI
  veterinarianFullName: string; //extra field UI....
  assignmentStatus: ReservationAssignmentStatuses | undefined;
  isWalkIn: boolean;
  addToWaitlist: boolean | undefined;
}

export class AppointmentRequest implements ModelRecord<IAppointmentRequest> {
  reservationId: number | undefined;
  hospitalId: number;
  clientId: number;
  petId: number;
  duration: number;
  appointmentTypeId: number;
  appointmentTypeName: string;
  veterinarianId: number | undefined;
  hospitalScheduletemplateId: number | undefined;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  reasonForVisit: string;
  veterinarianFullName: string;
  notes: string;
  appointmentStatus: AppointmentStatus;
  statusId: number;
  length: string;
  assignmentStatus: ReservationAssignmentStatuses | undefined;
  isWalkIn: boolean;
  addToWaitlist: boolean | undefined;
  rowVersion: string;

  constructor(params: Partial<IAppointmentRequest>) {
    const {
      reservationId = undefined,
      hospitalId = 0,
      clientId = 0,
      petId = 0,
      duration = 900,
      appointmentTypeId = 0,
      appointmentTypeName = '',
      veterinarianId = undefined,
      hospitalScheduletemplateId = undefined,
      startDate = DateTime.utc().toISO(),
      startTime = DateTime.utc().toISO(),
      endDate = DateTime.utc().toISO(),
      endTime = DateTime.utc().toISO(),
      reasonForVisit = '',
      notes = '',
      length = '',
      appointmentStatus = AppointmentStatus.Booked,
      statusId = 1,
      veterinarianFullName = '',
      assignmentStatus = ReservationAssignmentStatuses.Unassigned,
      isWalkIn = false,
      addToWaitlist = undefined,
      rowVersion = '',
    } = params;

    this.reservationId = reservationId;
    this.hospitalId = hospitalId;
    this.clientId = clientId;
    this.petId = petId;
    this.veterinarianFullName = veterinarianFullName;
    this.appointmentTypeId = appointmentTypeId;
    this.appointmentTypeName = appointmentTypeName;
    this.veterinarianId = veterinarianId;
    this.hospitalScheduletemplateId = hospitalScheduletemplateId;
    this.startDate = startDate;
    this.endDate = endDate;
    this.reasonForVisit = reasonForVisit;
    this.notes = notes;
    this.duration = duration;
    this.startTime = startTime;
    this.endTime = endTime;
    this.length = length;
    this.appointmentStatus = appointmentStatus;
    this.assignmentStatus = assignmentStatus;
    this.statusId = statusId;
    this.isWalkIn = isWalkIn;
    this.addToWaitlist = addToWaitlist;
    this.rowVersion = rowVersion;
  }

  /**
   * Provides a dropdown list of all appointment status options that exist provided the business rules exist
   * @returns all appointment status options applicable for the current appointment
   */
  getAvailableAppointmentStatuses(
    isAppointmentForCurrentDay?: boolean,
  ): ISelectOption[] {
    let appointmentStatuses: ISelectOption[] = [];
    if (this.reservationId == 0) {
      appointmentStatuses = filter(
        AppointmentStatusOptions,
        (appointmentStatus) => {
          return includes(
            [
              'Confirmed',
              'Booked',
              isAppointmentForCurrentDay ? 'Walk-in' : '',
            ],
            appointmentStatus.label,
          );
        },
      );
    } else if (this.statusId == 5) {
      appointmentStatuses = filter(
        AppointmentStatusOptions,
        (appointmentStatus) => {
          return includes(['Cancelled', 'Showed'], appointmentStatus.label);
        },
      );
    } else if (this.statusId > 3) {
      appointmentStatuses = filter(
        AppointmentStatusOptions,
        (appointmentStatus) => {
          return includes(
            [
              AppointmentStatus.statusFormattedName(
                AppointmentStatus[this.statusId],
              ),
            ],
            appointmentStatus.label,
          );
        },
      );
    } else {
      appointmentStatuses = filter(
        AppointmentStatusOptions,
        (appointmentStatus) => {
          return includes(
            ['Confirmed', 'Booked', 'Cancelled'],
            appointmentStatus.label,
          );
        },
      );
    }

    return appointmentStatuses;
  }

  static FromListingModel(hospitalId: number, model: AppointmentListingModel) {
    return new AppointmentRequest({
      reservationId: model.reservationId,
      hospitalId: hospitalId,
      clientId: model.clientId,
      petId: model.petId,
      appointmentTypeId: model.appointmentTypeId,
      appointmentTypeName: model.appointmentTypeName,
      veterinarianId: model.veterinarianId,
      hospitalScheduletemplateId: model.hospitalScheduleTemplateId,
      startDate: model.startDate,
      endDate: model.endDate,
      reasonForVisit: model.reasonForVisit,
      notes: model.notes,
      duration:
        model.startDate != undefined && model.endDate != undefined
          ? DateTimeUtils.getDuration(model.startDate, model.endDate, 'seconds')
          : 900,
      statusId: model.appointmentStatusId,
      appointmentStatus:
        model.appointmentStatusId == AppointmentStatus.Walk_In
          ? AppointmentStatus.Showed
          : model.appointmentStatusId,
      assignmentStatus: model.assignmentStatus,
      isWalkIn:
        model.appointmentStatusId == AppointmentStatus.Walk_In ? true : false,
      rowVersion: model.rowVersion,
      addToWaitlist: model.addToWaitlist,
    });
  }
}
